/**
 * El flujo "Mi IA no está respondiendo bien": el usuario copia un prompt, lo pega en la
 * conversación donde su Claude/ChatGPT le respondió mal, y nos trae la respuesta.
 *
 * Quien llega acá acaba de tener una conversación frustrante y NO quiere leer. Tres pasos, dos
 * clicks. Dos detalles que hacen la diferencia:
 *
 * - Al copiar, la card **avanza sola**: scroll + foco al textarea del paso 3. Copiar y no saber
 *   qué sigue es exactamente donde se cae la gente.
 * - El paso 3 **no es obligatorio**: que la IA no haya respondido nada ES el bug que queremos ver.
 */
import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

export const FeedbackIaBugCard = ({prompt, aiResponse, onAiResponseChange}) => {
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const responseRef = useRef(null);
    const promptText = prompt || t('feedback.ia_bug.prompt_fallback');

    const advanceToStepThree = () => {
        if (!responseRef.current) return;
        responseRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'});
        responseRef.current.focus();
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(promptText);
            setCopied(true);
            setCopyFailed(false);
            setTimeout(() => setCopied(false), 2000);
            advanceToStepThree();
        } catch (_) {
            // Contexto no seguro o permiso denegado: el `<pre>` queda seleccionable y se lo
            // decimos. Nunca un botón que no hace nada.
            setCopyFailed(true);
        }
    };

    return (
        <div className="feedback-card -ia">
            <p className="feedback-card__title">{t('feedback.ia_bug.title')}</p>
            <ol className="feedback-steps">
                <li>
                    {t('feedback.ia_bug.step1')}
                    <div className="feedback-prompt">
                        <pre>{promptText}</pre>
                        <button type="button" className="feedback-prompt__copy" onClick={handleCopy}>
                            {copied ? t('feedback.ia_bug.copied') : t('feedback.ia_bug.copy')}
                        </button>
                    </div>
                    {copyFailed && (
                        <p className="feedback-card__hint" role="alert">
                            {t('feedback.ia_bug.copy_manual')}
                        </p>
                    )}
                </li>
                <li>{t('feedback.ia_bug.step2')}</li>
                <li>
                    {t('feedback.ia_bug.step3')}
                    <textarea
                        ref={responseRef}
                        value={aiResponse}
                        onChange={(event) => onAiResponseChange(event.target.value)}
                        placeholder={t('feedback.ia_bug.response_placeholder')}
                    />
                    <p className="feedback-card__hint">{t('feedback.ia_bug.no_answer')}</p>
                </li>
            </ol>
        </div>
    );
};

export default FeedbackIaBugCard;
